/*
  Unicarat mega menu: opens the desktop header menus on hover, CaratLane-style.

  - Mouse: hovering an item opens its panel after a short intent delay, moving
    along the bar swaps panels instantly, and leaving the item and its panel
    closes it. Clicking an item that links somewhere (e.g. Ring -> its
    collection) follows the link.
  - Touch and keyboard keep Dawn's behaviour: tap / Enter toggles a panel,
    Esc closes it, tabbing out closes it.
  - Theme editor: selecting a "Mega menu promo" block opens its panel.

  Markup: snippets/header-mega-menu.liquid. Each item keeps Dawn's
  <header-menu><details> so the sticky header and global.js still manage it.
*/
if (!customElements.get('uc-mega-menu')) {
  const OPEN_DELAY = 90;
  const CLOSE_DELAY = 180;

  customElements.define(
    'uc-mega-menu',
    class UcMegaMenu extends HTMLElement {
      connectedCallback() {
        this.hover = this.dataset.hover === 'true';
        this.items = Array.from(this.querySelectorAll('.uc-mm__item--parent'));
        this.openItem = null;
        this.pinned = null;
        this.timer = null;
        this.pointerType = '';

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeyup = this.onDocumentKeyup.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onBlockDeselect = this.onBlockDeselect.bind(this);

        this.items.forEach((item) => {
          const details = item.querySelector('details');
          const summary = details.querySelector('summary');
          item.addEventListener('pointerenter', (event) => this.onEnter(event, item));
          item.addEventListener('pointerleave', (event) => this.onLeave(event, item));
          summary.addEventListener('pointerdown', (event) => (this.pointerType = event.pointerType));
          summary.addEventListener('keydown', () => (this.pointerType = ''));
          summary.addEventListener('click', (event) => this.onSummaryClick(event, item));
          details.addEventListener('toggle', () => this.onToggle(item));
        });

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keyup', this.onDocumentKeyup);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        this.warmImages();
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keyup', this.onDocumentKeyup);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      detailsOf(item) {
        return item.querySelector('details');
      }

      onEnter(event, item) {
        if (!this.hover || event.pointerType !== 'mouse' || this.pinned) return;
        clearTimeout(this.timer);
        if (this.openItem === item) return;
        if (this.openItem) {
          this.open(item, true);
        } else {
          this.timer = setTimeout(() => this.open(item, false), OPEN_DELAY);
        }
      }

      onLeave(event, item) {
        if (!this.hover || event.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        if (this.openItem !== item || this.pinned) return;
        this.timer = setTimeout(() => this.close(item), CLOSE_DELAY);
      }

      onSummaryClick(event, item) {
        const viaMouse = event.detail > 0 && this.pointerType === 'mouse';
        this.pointerType = '';
        if (!this.hover || !viaMouse) return;

        // With hover on, a click never collapses the menu: it follows the item's link
        event.preventDefault();
        const url = event.currentTarget.dataset.url;
        if (url) {
          if (event.metaKey || event.ctrlKey || event.shiftKey) window.open(url, '_blank');
          else window.location.href = url;
          return;
        }
        clearTimeout(this.timer);
        if (this.openItem !== item) this.open(item, Boolean(this.openItem));
        event.currentTarget.setAttribute('aria-expanded', 'true');
      }

      onToggle(item) {
        const details = this.detailsOf(item);
        if (details.open) {
          if (this.openItem && this.openItem !== item) this.close(this.openItem);
          this.openItem = item;
          this.fitPanel(details);
        } else if (this.openItem === item) {
          this.openItem = null;
          if (this.pinned === item) this.pinned = null;
          this.classList.remove('is-switching');
        }
      }

      onDocumentClick(event) {
        if (this.openItem && !this.contains(event.target)) this.close(this.openItem);
      }

      // Esc dismisses a panel opened by hover too (global.js only handles focus inside it)
      onDocumentKeyup(event) {
        if (event.key !== 'Escape' || !this.openItem || this.openItem.contains(document.activeElement)) return;
        this.close(this.openItem);
      }

      onBlockSelect(event) {
        const details = event.target.closest && event.target.closest('.uc-mm__details');
        if (!details || !this.contains(details)) return;
        const item = details.closest('.uc-mm__item');
        this.open(item, false);
        this.pinned = item;
      }

      onBlockDeselect(event) {
        const details = event.target.closest && event.target.closest('.uc-mm__details');
        if (!details || !this.contains(details)) return;
        this.close(details.closest('.uc-mm__item'));
      }

      open(item, switching) {
        clearTimeout(this.timer);
        this.items.forEach((other) => other !== item && this.close(other));
        this.classList.toggle('is-switching', switching);
        const details = this.detailsOf(item);
        this.fitPanel(details);
        details.setAttribute('open', '');
        details.querySelector('summary').setAttribute('aria-expanded', 'true');
        this.openItem = item;
      }

      close(item) {
        const details = this.detailsOf(item);
        if (details.hasAttribute('open')) {
          details.removeAttribute('open');
          details.querySelector('summary').setAttribute('aria-expanded', 'false');
        }
        if (this.openItem === item) this.openItem = null;
        if (this.pinned === item) this.pinned = null;
        if (!this.openItem) this.classList.remove('is-switching');
      }

      // Keep a tall panel inside the window; it scrolls on its own instead
      fitPanel(details) {
        const panel = details.querySelector('.uc-mm__panel');
        const header = this.closest('.header-wrapper');
        if (!panel || !header) return;
        const top = Math.max(0, header.getBoundingClientRect().bottom);
        panel.style.setProperty('--uc-mm-max-height', `${Math.max(240, window.innerHeight - top - 16)}px`);
      }

      // Fetch the promo images once the page has loaded so panels open complete
      warmImages() {
        const desktop = window.matchMedia('(min-width: 990px)').matches;
        const saveData = navigator.connection && navigator.connection.saveData;
        if (!desktop || saveData) return;
        const warm = () =>
          this.querySelectorAll('.uc-mm__card img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        const whenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 3000 }) : setTimeout(warm, 1500));
        if (document.readyState === 'complete') whenIdle();
        else window.addEventListener('load', whenIdle, { once: true });
      }
    }
  );
}
